import { type RouterHistory, createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import { ERROR_TYPE, reportError } from './errorReporting.ts';
import { USER_FILTERS, type UserFilter } from './format.ts';
import { AuditPage } from './pages/audit.tsx';
import { BookingsPage } from './pages/bookings.tsx';
import { BuildingPage } from './pages/building.tsx';
import { BuildingsPage } from './pages/buildings.tsx';
import { ContractsPage } from './pages/contracts.tsx';
import { DepartmentsPage } from './pages/departments.tsx';
import { DevicesPage } from './pages/devices.tsx';
import { HomePage } from './pages/home.tsx';
import { IntegrationsPage } from './pages/integrations.tsx';
import { OrganizationsPage } from './pages/organizations.tsx';
import { UsersPage } from './pages/users.tsx';
import { Shell } from './shell.tsx';

const root = createRootRoute({ component: Shell });

const text = (value: unknown): string | undefined => (typeof value === 'string' && value ? value : undefined);

const buildingRoute = createRoute({
  getParentRoute: () => root,
  path: '/buildings/$buildingId',
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({ tab: text(search.tab) }),
  component: function Building() {
    const { buildingId } = buildingRoute.useParams();
    return <BuildingPage buildingId={buildingId} />;
  },
});

const usersRoute = createRoute({
  getParentRoute: () => root,
  path: '/users',
  validateSearch: (search: Record<string, unknown>): { filter?: UserFilter; q?: string } => ({
    filter: USER_FILTERS.find((filter) => filter === search.filter),
    q: text(search.q),
  }),
  component: UsersPage,
});

const bookingsRoute = createRoute({
  getParentRoute: () => root,
  path: '/bookings',
  validateSearch: (
    search: Record<string, unknown>,
  ): { buildingId?: string; day?: string; status?: string } => ({
    buildingId: text(search.buildingId),
    day: text(search.day),
    status: text(search.status),
  }),
  component: BookingsPage,
});

const devicesRoute = createRoute({
  getParentRoute: () => root,
  path: '/devices',
  validateSearch: (search: Record<string, unknown>): { buildingId?: string } => ({
    buildingId: text(search.buildingId),
  }),
  component: DevicesPage,
});

const auditRoute = createRoute({
  getParentRoute: () => root,
  path: '/audit',
  validateSearch: (search: Record<string, unknown>): { entity?: string; q?: string } => ({
    entity: text(search.entity),
    q: text(search.q),
  }),
  component: AuditPage,
});

const routes = [
  createRoute({ getParentRoute: () => root, path: '/', component: HomePage }),
  createRoute({ getParentRoute: () => root, path: '/organizations', component: OrganizationsPage }),
  createRoute({ getParentRoute: () => root, path: '/buildings', component: BuildingsPage }),
  buildingRoute,
  bookingsRoute,
  usersRoute,
  createRoute({ getParentRoute: () => root, path: '/departments', component: DepartmentsPage }),
  devicesRoute,
  createRoute({ getParentRoute: () => root, path: '/contracts', component: ContractsPage }),
  createRoute({ getParentRoute: () => root, path: '/integrations', component: IntegrationsPage }),
  auditRoute,
];

/** The console's routes; a test or a Storybook story passes a memory history to open one page. */
export function createConsoleRouter(history?: RouterHistory) {
  return createRouter({
    routeTree: root.addChildren(routes),
    defaultOnCatch: (error) => reportError(error, ERROR_TYPE.routeRender),
    ...(history ? { history } : {}),
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createConsoleRouter>;
  }
}
